import { isDefined } from '@morpho-org/morpho-ts'

/**
 * Calculates how many skeleton rows to show in a table based on pagination state.
 * On the last page, shows only the remaining rows. Otherwise shows full page size.
 * @param first - Number of items per page
 * @param totalItemsCount - Total number of items across all pages
 * @param currentPage - Current page index (0-based)
 * @returns Number of skeleton rows to render
 */
export const getTableSkeletonRowCount = (
  first: number,
  totalItemsCount: number | undefined | null,
  currentPage: number | undefined | null
) => {
  const skeletonRows = first

  const lastPage = totalItemsCount && Math.ceil(totalItemsCount / first)

  const _currentPage = isDefined(currentPage) ? currentPage + 1 : currentPage

  if (lastPage && _currentPage === lastPage) {
    return totalItemsCount % first || first
  }
  return skeletonRows
}

/**
 * Calculates how many skeleton rows to show in an infinite scroll table.
 * During full table loading, shows full page size. During next-page fetch,
 * shows only the remaining items (capped at page size).
 */
export const getInfiniteSkeletonRowCount = (
  pageSize: number,
  {
    isLoadingFullTable,
    isFetchingNextPage,
    totalCount,
    loadedCount
  }: {
    isLoadingFullTable: boolean
    isFetchingNextPage: boolean
    totalCount: number | null | undefined
    loadedCount: number
  }
): number => {
  if (isLoadingFullTable) return pageSize
  if (!isFetchingNextPage) return 0
  if (totalCount == null) return pageSize
  return Math.min(pageSize, Math.max(0, totalCount - loadedCount))
}
